import { useState } from 'react';
import { Avatar, Button, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  FeatureSubscriptionType,
  PortalPermissions,
  useUser_GetPortalAccountUsersQuery,
} from 'queries';

import Spinner from 'common/spinner';
import InviteUserModal from 'pages/portal-account/components/manage-portal-users/invite-user-modal';
import { canAddPointPerson, canCollabWithTeamMembers } from 'utils/feature-checker';
import { NotificationSeverity, showAlert, showToast } from 'utils/notification-manager';
import useHasPortalPermission from 'utils/useHasPortalPermission';

import AssignToEstateModal from './assign-to-estate-modal';

const useStyles = makeStyles(() => ({
  container: {
    display: 'flex',
    gap: '24px',
    flexWrap: 'wrap',
  },
  avatar: {
    fontSize: '32px',
    background: '#1C2541',
    color: '#F5F5F5',
    width: '72px',
    height: '72px',
    marginRight: '10px',
    marginBottom: '32px',
  },
  buttonPrimary: {
    backgroundColor: '#5ED1F0',
    color: '#1C2541',
    borderRadius: '24px',
    boxShadow: 'none',
    fontSize: '12px',
    px: '16px',
    width: '184px',
    border: '2px solid #5ED1F0',
  },
  teamItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexDirection: 'column',
    width: '224px',
    height: '282px',
    columnGap: '24px',
    border: '1px solid #E0E0E0',
    borderRadius: '16px',
    padding: '24px 24px 32px 24px',
  },
  title: {
    marginBottom: '24px',
    textAlign: 'center',
  },
  teamItemHeader: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
  },
}));

type Props = {
  portalId: string;
  featureSubscriptionType: FeatureSubscriptionType;
};

const EstateTeamMembers = ({ portalId, featureSubscriptionType }: Props) => {
  const classes = useStyles();
  const hasPortalPermissionAdmin = useHasPortalPermission(PortalPermissions.Admin);
  const [userIdToAssignToEstate, setUserIdToAssignToEstate] = useState<string | null>(null);
  const [showInviteUserModal, setShowInviteUserModal] = useState(false);

  const { data: usersData, loading: loadingUsersData } = useUser_GetPortalAccountUsersQuery({
    variables: {
      input: {
        portalId,
        includeAccountDisabledUsers: false,
        includeClientUsers: false,
      },
    },
    onError: (e) => {
      showAlert({
        message: 'Error getting portal account users: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const onSetAssignUserIdToEstateClicked = (userId: string) => {
    if (!canAddPointPerson(featureSubscriptionType)) {
      showToast({
        message: 'Please upgrade your account to add a point person to an estate.',
        severity: NotificationSeverity.WARNING,
      });
      return;
    }
    setUserIdToAssignToEstate(userId);
  };

  const onAddTeamMemberClicked = () => {
    if (!canCollabWithTeamMembers(featureSubscriptionType)) {
      showToast({
        message: 'Please upgrade your account to collaborate with team members.',
        severity: NotificationSeverity.WARNING,
      });
      return;
    }

    setShowInviteUserModal(true);
  };

  if (loadingUsersData) {
    return <Spinner />;
  }

  return (
    <div className={classes.container}>
      {usersData?.data?.map((user) => (
        <div className={classes.teamItem} key={user.id}>
          <div className={classes.teamItemHeader}>
            {user.avatarUrl ? (
              <Avatar className={classes.avatar} src={user.avatarUrl} />
            ) : (
              <Avatar className={classes.avatar}>{user.firstName.charAt(0)}</Avatar>
            )}

            <Typography className={classes.title}>
              {user.firstName} {user.lastName.charAt(0)}.
            </Typography>
          </div>
          <Button
            component="span"
            className={classes.buttonPrimary}
            color="primary"
            variant="contained"
            size="small"
            onClick={() => onSetAssignUserIdToEstateClicked(user.id)}
          >
            Assign to Matter
          </Button>
        </div>
      ))}

      {hasPortalPermissionAdmin && (
        <div className={classes.teamItem}>
          <div className={classes.teamItemHeader}>
            <img src="/invite-team-members-empty.svg" alt="add-team-member" />
            <Typography className={classes.title}>
              Invite people to start assigning tasks.
            </Typography>
          </div>
          <Button
            component="span"
            className={classes.buttonPrimary}
            color="primary"
            variant="contained"
            size="small"
            onClick={onAddTeamMemberClicked}
          >
            Add Team Members
          </Button>
        </div>
      )}

      {userIdToAssignToEstate && (
        <AssignToEstateModal
          userId={userIdToAssignToEstate}
          onClose={() => setUserIdToAssignToEstate(null)}
          portalId={portalId}
        />
      )}
      {hasPortalPermissionAdmin && (
        <InviteUserModal showModal={showInviteUserModal} setShowModal={setShowInviteUserModal} />
      )}
    </div>
  );
};

export default EstateTeamMembers;
